"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useAuth } from "../../../hooks/useAuth";
import { channelsAPI, handleApiError } from "../../../services/api";
import "./ManageJoinRequestsModal.css";

const ManageJoinRequestsModal = ({ isOpen, onClose, channelId, channelName, userRole }) => {
  const { t } = useTranslation();
  const { getUserType, user } = useAuth();
  
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8',
      'VETERINARIAN': '#00D4B3',
      'SERVICE_PROVIDER': '#D65745',
      'SERVICE_PROVIDER': '#D65745',
      'CLINIC_MODERATOR': '#2A8AD2',
    };
    return userTypeConfig[userType] || '#8548A8';
  };
  const userTypeColor = getUserTypeColor(currentUserType);
  
  const [joinRequests, setJoinRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [approvingRequestId, setApprovingRequestId] = useState(null);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);

  const canModerateRequests = userRole === "owner" || userRole === "admin";

  const mapJoinRequest = (request) => {
    const userData = request.user || {};
    const userId = userData.id || userData.user_id || request.user_id;
    const isSelf = userId === (user?.id || user?.user_id);
    
    // Get fallback name for current user from auth context
    const authUserName = isSelf ? (user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.username) : null;

    // Robust name resolution
    const resolvedName = 
      userData.name || 
      [userData.first_name, userData.last_name].filter(Boolean).join(" ").trim() ||
      userData.username ||
      request.name || // Check if flattened on request object
      [request.first_name, request.last_name].filter(Boolean).join(" ").trim() ||
      request.username ||
      authUserName ||
      "Unknown";

    return {
      id: request.id,
      userId: userId,
      name: resolvedName,
      profileImage: userData.profile_image_url || userData.avatar || (isSelf ? user?.profile_image_url || user?.avatar : null) || "/icons/person.png",
      userType: userData.user_type || userData.type || (isSelf ? user?.user_type || user?.type : null) || "PET_GUARDIAN",
      message: request.message || "",
      requestedAt: request.created_at || request.createdAt || null,
    };
  };

  const formatDateTime = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleString();
    } catch (e) {
      return value;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const requestsResponse = canModerateRequests
          ? await channelsAPI.getChannelJoinRequests(channelId, {
              page: 1,
              per_page: 100,
            })
          : null;

        if (cancelled) {
          return;
        }

        if (canModerateRequests && requestsResponse?.data?.data) {
          setJoinRequests(requestsResponse.data.data.map(mapJoinRequest));
        } else {
          setJoinRequests([]);
        }
      } catch (error) {
        if (!cancelled) {
          handleApiError(error);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    const handleJoinRequestSubmitted = (event) => {
      const { channelId: eventChannelId } = event.detail;
      if (Number(eventChannelId) === Number(channelId)) {
        loadData();
      }
    };

    window.addEventListener('channel:join-request-submitted', handleJoinRequestSubmitted);

    return () => {
      cancelled = true;
      window.removeEventListener('channel:join-request-submitted', handleJoinRequestSubmitted);
    };
  }, [isOpen, channelId, canModerateRequests]);


  const handleApproveJoinRequest = async (requestId) => {
    setApprovingRequestId(requestId);
    try {
      await channelsAPI.approveJoinRequest(channelId, requestId);
      setJoinRequests(prev => prev.filter(item => item.id !== requestId));
      // Dispatch event to refresh channels in sidebar (for the user whose request was approved)
      const request = joinRequests.find(item => item.id === requestId);
      window.dispatchEvent(new CustomEvent('channel:join-request-accepted', {
        detail: { channelId, requestId, userId: request?.userId }
      }));
    } catch (error) {
      handleApiError(error);
    } finally {
      setApprovingRequestId(null);
    }
  };

  const handleRejectJoinRequest = async (requestId) => {
    setRejectingRequestId(requestId);
    try {
      await channelsAPI.rejectJoinRequest(channelId, requestId);
      setJoinRequests(prev => prev.filter(item => item.id !== requestId));
    } catch (error) {
      handleApiError(error);
    } finally {
      setRejectingRequestId(null);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="manage-join-requests-modal-overlay" onClick={onClose}>
      <div className="manage-join-requests-modal" onClick={(e) => e.stopPropagation()} style={{ '--user-type-color': userTypeColor }}>
        <div className="manage-join-requests-modal-header">
          <div className="manage-join-requests-modal-header-content">
            <h2 className="manage-join-requests-modal-title">
              {t("manageJoinRequests.title", "Pending Join Requests")}
            </h2>
            <span className="manage-join-requests-modal-subtitle">
              {joinRequests.length}{" "}
              {joinRequests.length === 1
                ? t("manageJoinRequests.singleRequest", "request")
                : t("manageJoinRequests.multiRequest", "requests")}
            </span>
          </div>
          <button className="manage-join-requests-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="manage-join-requests-modal-content">
          {isLoading ? (
            <div className="manage-join-requests-modal-loading">
              <div className="loading-spinner"></div>
              <p>{t("manageJoinRequests.loading", "Loading join requests...")}</p>
            </div>
          ) : (
            <div className="manage-join-requests-modal-body">
              {canModerateRequests ? (
                joinRequests.length > 0 ? (
                  <div className="manage-join-requests-list">
                    {joinRequests.map(request => (
                      <div key={request.id} className="manage-join-requests-item">
                        <div className="manage-join-requests-info">
                          <img
                            src={request.profileImage || "/icons/person.png"}
                            alt={request.name}
                            className="manage-join-requests-avatar"
                            onError={e => {
                              e.target.src = "/icons/person.png";
                            }}
                          />
                          <div className="manage-join-requests-text">
                            <div className="manage-join-requests-name-row">
                              <span className="manage-join-requests-name">
                                {request.name}
                              </span>
                              {request.requestedAt && (
                                <span className="manage-join-requests-time">
                                  {formatDateTime(request.requestedAt)}
                                </span>
                              )}
                            </div>
                            {request.message && (
                              <p className="manage-join-requests-message">
                                "{request.message}"
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="manage-join-requests-actions">
                          <button
                            className="manage-join-requests-approve-btn"
                            onClick={() => handleApproveJoinRequest(request.id)}
                            disabled={
                              approvingRequestId === request.id ||
                              rejectingRequestId === request.id
                            }
                          >
                            {approvingRequestId === request.id
                              ? t("manageJoinRequests.approving", "Approving...")
                              : t("manageJoinRequests.approve", "Approve")}
                          </button>
                          <button
                            className="manage-join-requests-reject-btn"
                            onClick={() => handleRejectJoinRequest(request.id)}
                            disabled={
                              approvingRequestId === request.id ||
                              rejectingRequestId === request.id
                            }
                          >
                            {rejectingRequestId === request.id
                              ? t("manageJoinRequests.rejecting", "Rejecting...")
                              : t("manageJoinRequests.reject", "Reject")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="manage-join-requests-modal-empty">
                    <p>{t("manageJoinRequests.noPendingRequests", "No pending join requests.")}</p>
                  </div>
                )
              ) : (
                <div className="manage-join-requests-modal-empty">
                  <p>
                    {t(
                      "manageJoinRequests.noPermissions",
                      "Only owners or admins can review join requests."
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ManageJoinRequestsModal;

